import React, { useEffect, useRef, useState } from "react";
import { Pipette } from "lucide-react";
import { cn } from "../lib/utils";
import { colorAlpha, colorFamily, parseColor, rgbaToHex, withAlpha } from "../lib/color";

export interface ColorPreset {
  value: string;
  label?: string;
}

/** Checkerboard-backed swatch so translucent colours read as translucent. */
function Swatch({ value, className }: { value: string; className?: string }) {
  return (
    <span
      className={cn(
        "relative block overflow-hidden rounded border",
        "bg-[linear-gradient(45deg,#e5e5e5_25%,transparent_25%,transparent_75%,#e5e5e5_75%),linear-gradient(45deg,#e5e5e5_25%,transparent_25%,transparent_75%,#e5e5e5_75%)]",
        "bg-[length:8px_8px] bg-[position:0_0,4px_4px]",
        className
      )}
    >
      <span className="absolute inset-0" style={{ background: parseColor(value) ? value : undefined }} />
    </span>
  );
}

/**
 * The shared colour picker: a swatch trigger opening a panel with the tenant's
 * preset palette at the top, modifiers (opacity), and custom input below
 * (native picker + free text accepting hex, rgb() and oklch()). Sections are
 * stacked so future sources (e.g. AI-suggested colours) slot in.
 */
export function ColorPicker({
  value,
  onChange,
  presets = [],
  ariaLabel = "Colour",
  className,
}: {
  /** Current CSS colour ("" allowed — renders an empty swatch). */
  value: string;
  onChange: (value: string) => void;
  /** Preset swatches shown at the top (e.g. the tenant palette). */
  presets?: ColorPreset[];
  ariaLabel?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const rootRef = useRef<HTMLDivElement>(null);

  // Keep the free-text draft following the applied value.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const parsed = parseColor(value);
  const alphaPercent = Math.round(colorAlpha(value) * 100);

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed || colorFamily(trimmed)) onChange(trimmed);
  };

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-background p-1 hover:bg-muted"
      >
        <Swatch value={value} className="h-full w-full" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border bg-background p-3 shadow-lg">
          {presets.length > 0 && (
            <div className="mb-3">
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Presets</p>
              <div className="flex flex-wrap gap-1.5">
                {presets.map((preset, index) => (
                  <button
                    key={`${preset.value}-${index}`}
                    type="button"
                    title={preset.label ?? preset.value}
                    onClick={() => onChange(preset.value)}
                    className={cn(
                      "h-7 w-7 rounded-md p-0.5 ring-offset-1 hover:ring-2 hover:ring-ring",
                      value.trim() === preset.value && "ring-2 ring-primary"
                    )}
                  >
                    <Swatch value={preset.value} className="h-full w-full" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-3">
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground">Opacity</p>
              <span className="text-xs tabular-nums text-muted-foreground">{alphaPercent}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={alphaPercent}
              disabled={!parsed}
              aria-label="Opacity"
              onChange={(e) => onChange(withAlpha(value, Number(e.target.value) / 100))}
              className="w-full accent-primary"
            />
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">Custom</p>
            <div className="flex items-center gap-2">
              <label
                className="relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border hover:bg-muted"
                title="Pick a custom colour"
              >
                <Pipette className="h-4 w-4 text-muted-foreground" />
                <input
                  type="color"
                  aria-label="Custom colour"
                  value={parsed ? rgbaToHex(parsed) : "#000000"}
                  onChange={(e) => onChange(withAlpha(e.target.value, colorAlpha(value)))}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
              </label>
              <input
                type="text"
                value={draft}
                spellCheck={false}
                aria-label="Colour value"
                placeholder="#2563eb, rgb(…), hsl(…), oklch(…)"
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitDraft}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitDraft();
                  }
                }}
                className={cn(
                  "h-9 w-full rounded-md border bg-background px-2 font-mono text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  draft.trim() && !colorFamily(draft) && "border-destructive"
                )}
              />
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
              Hex, rgb(), hsl() and oklch() are supported.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
